import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { MapPin, Sparkles, Pencil, Trash2, Calendar, CreditCard, ShoppingBag, Tag, CheckCircle2, Heart, Star, Receipt, Settings, MessageSquarePlus, Send, Route, Navigation } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PLANS, hasFreePass } from "@/lib/constants";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { toast } from "sonner";

function planLabel(plan) {
  if (!plan || !plan.type) return "No active plan";
  if (plan.type === "trial") return "Free weekend trial";
  const map = { buyer_weekly: "Weekly Shopper Pass", buyer_monthly: "Monthly Unlimited", seller_pro_monthly: "Pro Seller (Monthly)" };
  return map[plan.type] || plan.type;
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", username: "", address: "", phone: "", city: "", zip: "", role: "buyer" });
  const [listings, setListings] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", username: user.username || "", address: user.address || "", phone: user.phone || "", city: user.city || "", zip: user.zip || "", role: user.role === "admin" ? "admin" : user.role });
  }, [user]);

  const [saved, setSaved] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [charges, setCharges] = useState([]);
  const [savedCard, setSavedCard] = useState(null);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "settings");
  useEffect(() => { const t = sp.get("tab"); if (t) setTab(t); }, [sp]);
  const logErr = (what) => (err) => console.error(`Failed to load ${what}:`, err);
  const loadListings = useCallback(() => api.get("/my/listings").then(({ data }) => setListings(data)).catch(logErr("listings")), []);
  const loadSaved = useCallback(() => api.get("/my/saved").then(({ data }) => setSaved(data)).catch(logErr("saved sales")), []);
  const loadSavedCard = useCallback(() => api.get("/payments/saved-card").then(({ data }) => setSavedCard(data.saved_card)).catch(logErr("saved card")), []);
  const loadRoutes = useCallback(() => api.get("/my/routes").then(({ data }) => setRoutes(data)).catch(logErr("routes")), []);
  const openRoute = (r) => navigate("/browse", { state: { loadRoute: r.stops } });
  const deleteRoute = async (rid) => { try { await api.delete(`/my/routes/${rid}`); loadRoutes(); toast.success("Route deleted"); } catch { toast.error("Could not delete route"); } };
  useEffect(() => { loadListings(); loadSaved(); loadSavedCard(); loadRoutes(); api.get("/my/reviews").then(({ data }) => setReviews(data)).catch(logErr("reviews")); api.get("/my/charges").then(({ data }) => setCharges(data)).catch(logErr("charges")); }, [loadListings, loadSaved, loadSavedCard, loadRoutes]); // eslint-disable-line react-hooks/exhaustive-deps
  const unsave = async (lid) => { try { await api.post(`/listings/${lid}/save`); await refreshUser(); loadSaved(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const [idea, setIdea] = useState("");
  const [siteStars, setSiteStars] = useState(0);
  const [siteComment, setSiteComment] = useState("");
  const [prefs, setPrefs] = useState(user?.preferences || {});
  useEffect(() => { setPrefs(user?.preferences || {}); }, [user]);
  const savePref = async (key, val) => { const next = { ...prefs, [key]: val }; setPrefs(next); try { await api.put("/auth/preferences", { preferences: { [key]: val } }); await refreshUser(); } catch { toast.error("Could not save preference"); } };
  const submitIdea = async () => {
    if (!idea.trim()) { toast.error("Write your idea first"); return; }
    try { await api.post("/suggestions", { message: idea }); toast.success("Sent to the Yard-skip team — thank you!"); setIdea(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitSiteReview = async () => {
    if (!siteStars) { toast.error("Pick a star rating"); return; }
    try { await api.post("/site-reviews", { stars: siteStars, comment: siteComment }); toast.success("Thanks for rating Yard-skip!"); setSiteComment(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, username: form.username, address: form.address, phone: form.phone, city: form.city, zip: form.zip };
      if (user.role !== "admin") payload.role = form.role;
      await api.put("/auth/profile", payload);
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cancelSub = async () => {
    try { await api.post("/subscriptions/cancel"); await refreshUser(); toast.success("Auto-renew turned off. Access stays until period ends."); }
    catch { toast.error("Could not update subscription"); }
  };

  const del = async (lid) => {
    try { await api.delete(`/listings/${lid}`); toast.success("Listing deleted"); loadListings(); }
    catch { toast.error("Could not delete"); }
  };

  const highlight = async (lid, key) => {
    if (savedCard) {
      try { await api.post("/payments/quick-charge", { lookup_key: key, listing_id: lid }); toast.success("Highlighted with your saved card ✨"); loadListings(); return; }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return; }
    }
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin, listing_id: lid });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unhighlight = async (lid) => { try { await api.post(`/listings/${lid}/unhighlight`); toast.success("Highlight turned off"); loadListings(); } catch { toast.error("Failed"); } };

  const activate = async (lid) => {
    if (savedCard) {
      try { await api.post("/payments/quick-charge", { lookup_key: "seller_weekly", listing_id: lid }); toast.success("Listing activated with your saved card"); loadListings(); return; }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return; }
    }
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "seller_weekly", origin_url: window.location.origin, listing_id: lid });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addCard = async () => {
    try { const { data } = await api.post("/payments/save-card", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeCard = async () => {
    try { await api.delete("/payments/saved-card"); setSavedCard(null); toast.success("Card removed"); }
    catch { toast.error("Could not remove card"); }
  };

  if (!user) return null;
  const plan = user.plan || {};

  return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-ys-sky text-white flex items-center justify-center text-2xl font-bold font-display overflow-hidden">
            {user.picture ? <img src={fileUrl(user.picture)} alt="" className="w-full h-full object-cover" /> : (user.name || user.email).slice(0, 1).toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold">{user.name || "My Profile"}</h1>
            <p className="text-slate-500 text-sm">{user.email}</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="rounded-full bg-slate-100 p-1 flex-wrap h-auto gap-1" data-testid="profile-tabs">
            <TabsTrigger value="settings" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-settings"><Settings className="w-4 h-4" />Settings</TabsTrigger>
            <TabsTrigger value="saved" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-saved"><Heart className="w-4 h-4" />Saved sales</TabsTrigger>
            <TabsTrigger value="routes" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-routes"><Route className="w-4 h-4" />Routes</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-reviews"><Star className="w-4 h-4" />Reviews</TabsTrigger>
            {(user.role === "seller" || user.role === "admin") && <TabsTrigger value="listings" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-listings"><Tag className="w-4 h-4" />My listings</TabsTrigger>}
            <TabsTrigger value="billing" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-billing"><Receipt className="w-4 h-4" />Billing</TabsTrigger>
            <TabsTrigger value="feedback" className="rounded-full gap-1.5 data-[state=active]:bg-white" data-testid="tab-feedback"><MessageSquarePlus className="w-4 h-4" />Feedback</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-6 space-y-6">
            {/* Plan card */}
            <Card className="p-6 rounded-2xl" data-testid="plan-status-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Current plan</p>
              <div className="font-display text-xl font-bold mt-1 flex items-center gap-2">
                {hasFreePass(user) ? <span data-testid="plan-free-pass-label">Pro Seller · Free pass</span> : planLabel(plan)}
                {hasFreePass(user) && <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" />Active</Badge>}
                {!hasFreePass(user) && plan.status === "active" && <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" />Active</Badge>}
                {plan.type === "trial" && <Badge className="bg-amber-100 text-amber-700 gap-1"><Sparkles className="w-3 h-3" />Trial</Badge>}
              </div>
              {hasFreePass(user) && <p className="text-sm text-slate-500 mt-1" data-testid="plan-free-pass-expiry">Gifted by admin — no card needed. {user.free_pass.expires ? `Free until ${new Date(user.free_pass.expires).toLocaleDateString()}` : "Never expires"}</p>}
              {!hasFreePass(user) && plan.expires_at && <p className="text-sm text-slate-500 mt-1">{plan.auto_renew ? "Renews" : "Expires"} {new Date(plan.expires_at).toLocaleDateString()}</p>}
              {plan.type === "trial" && plan.trial_end && <p className="text-sm text-slate-500 mt-1" data-testid="trial-window">Free access {new Date(plan.trial_start).toLocaleDateString()} – {new Date(plan.trial_end).toLocaleDateString()}. Pick a plan to keep going after your trial.</p>}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate("/pricing")} variant="outline" className="rounded-full gap-2" data-testid="change-plan-btn"><CreditCard className="w-4 h-4" />Plans</Button>
              {plan.auto_renew && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" className="rounded-full text-red-600" data-testid="cancel-sub-btn">Cancel auto-renew</Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Turn off auto-renew?</AlertDialogTitle><AlertDialogDescription>You'll keep access until the end of the current period, then your account resets.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel><AlertDialogAction onClick={cancelSub}>Turn off</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </Card>

        {/* Profile form */}
        <Card className="p-6 rounded-2xl">
          <h2 className="font-display text-lg font-bold mb-4">Account details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="profile-username-input" placeholder="your_handle" className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Address <span className="text-slate-400 font-normal text-xs">(for your listings)</span></Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address-input" className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city-input" className="mt-1.5 rounded-xl h-11" /></div>
            <div><Label>Zip</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="profile-zip-input" className="mt-1.5 rounded-xl h-11" /></div>
            {user.role !== "admin" && (
              <div><Label>Account type</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="profile-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="buyer">Shopper</SelectItem><SelectItem value="seller">Seller</SelectItem></SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="mt-5 rounded-full bg-ys-sky hover:bg-sky-700">{saving ? "Saving…" : "Save changes"}</Button>
        </Card>
            <Card className="p-6 rounded-2xl" data-testid="preferences-card">
              <h2 className="font-display text-lg font-bold mb-1">Preferences</h2>
              <p className="text-sm text-slate-500 mb-4">Control your Yard-skip experience.</p>
              <div className="divide-y divide-slate-100">
                {[
                  ["gps_enabled", "Use my GPS location", "Auto-find sales near you on the map"],
                  ["notifications_opt_out", "Opt out of reminder emails", "Stop the morning & evening saved-sale reminders"],
                  ["save_payment", "Save my payment info for faster checkout", "Securely remember my card via Stripe at next payment"],
                ].map(([k, title, desc]) => (
                  <div key={k} className="flex items-center justify-between py-3 gap-4">
                    <div><p className="font-medium text-sm">{title}</p><p className="text-xs text-slate-500">{desc}</p></div>
                    <Switch checked={!!prefs[k]} onCheckedChange={(v) => savePref(k, v)} data-testid={`pref-${k}`} />
                  </div>
                ))}
              </div>
              {(prefs.hidden_listings?.length || 0) > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-slate-500">{prefs.hidden_listings.length} hidden sale(s)</span>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => savePref("hidden_listings", [])} data-testid="clear-hidden-btn">Unhide all</Button>
                </div>
              )}
            </Card>
            <ChangePasswordCard hasPassword={user?.has_password !== false} />
            <Card className="p-6 rounded-2xl" data-testid="payment-method-card">
              <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2"><CreditCard className="w-5 h-5 text-ys-sky" />Payment method</h2>
              <p className="text-sm text-slate-500 mb-4">Save a card to renew a plan or highlight a listing in one tap.</p>
              {savedCard ? (
                <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="saved-card-row">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold uppercase">{savedCard.brand}</div>
                    <div><p className="font-medium">•••• {savedCard.last4}</p><p className="text-xs text-slate-500">Expires {savedCard.exp_month}/{savedCard.exp_year}</p></div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={addCard} data-testid="update-card-btn">Update</Button>
                    <Button variant="outline" size="sm" className="rounded-full text-red-600" onClick={removeCard} data-testid="remove-card-btn">Remove</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={addCard} data-testid="add-card-btn" className="rounded-full bg-ys-sky hover:bg-sky-700 gap-2"><CreditCard className="w-4 h-4" />Add a card</Button>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="saved" className="mt-6">
        <div>
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Heart className="w-5 h-5 text-red-500" />Saved sales</h2>
          {saved.length === 0 ? (
            <Card className="p-6 rounded-2xl text-center text-slate-400" data-testid="saved-empty">No saved sales yet. Tap the heart on any sale to save it — we'll email you the morning it goes live.</Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="saved-list">
              {saved.map((l) => (
                <Card key={l.id} className="p-4 rounded-2xl flex items-center justify-between gap-3" data-testid={`saved-${l.id}`}>
                  <div className="min-w-0"><p className="font-semibold truncate">{l.title}</p><p className="text-xs text-slate-500">{l.city}, {l.zip}</p></div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/listing/${l.id}`)} className="rounded-full h-8 text-xs">View</Button>
                    <Button size="sm" variant="ghost" onClick={() => unsave(l.id)} className="rounded-full h-8 text-xs text-red-500" data-testid={`unsave-${l.id}`}>Remove</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

          </TabsContent>

          <TabsContent value="routes" className="mt-6">
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Route className="w-5 h-5 text-ys-sky" />My saved routes</h2>
            {routes.length === 0 ? (
              <Card className="p-6 rounded-2xl text-center text-slate-400" data-testid="routes-empty">No saved routes yet. Build a route on the map, then tap <span className="font-semibold text-slate-500">Save</span> to keep your weekend itinerary here.</Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="routes-list">
                {routes.map((r) => (
                  <Card key={r.id} className="p-4 rounded-2xl" data-testid={`route-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{r.name}</p>
                        <p className="text-xs text-slate-500">{r.stops?.length || 0} stop{(r.stops?.length || 0) === 1 ? "" : "s"}{r.created_at ? ` · saved ${new Date(r.created_at).toLocaleDateString()}` : ""}</p>
                      </div>
                      <Badge className="bg-sky-100 text-ys-sky shrink-0">{r.stops?.length || 0}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(r.stops || []).slice(0, 4).map((s, i) => (
                        <span key={s.id || i} className="text-[11px] bg-slate-100 text-slate-500 rounded-full px-2 py-0.5 truncate max-w-[140px]">{i + 1}. {s.title}</span>
                      ))}
                      {(r.stops?.length || 0) > 4 && <span className="text-[11px] text-slate-400 px-1">+{r.stops.length - 4} more</span>}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={() => openRoute(r)} className="rounded-full h-8 text-xs bg-ys-sky hover:bg-sky-700 gap-1" data-testid={`open-route-${r.id}`}><Navigation className="w-3 h-3" />Open on map</Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteRoute(r.id)} className="rounded-full h-8 text-xs text-red-500 gap-1" data-testid={`delete-route-${r.id}`}><Trash2 className="w-3 h-3" />Delete</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-ys-gold" />My reviews</h2>
            {reviews.length === 0 ? (
              <Card className="p-6 rounded-2xl text-center text-slate-400" data-testid="reviews-empty">You haven't reviewed any sales yet. Rate a sale you visited from its listing page.</Card>
            ) : (
              <div className="space-y-3" data-testid="reviews-list">
                {reviews.map((r) => (
                  <Card key={r.id} className="p-4 rounded-2xl" data-testid={`my-review-${r.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <button onClick={() => navigate(`/listing/${r.listing_id}`)} className="font-semibold truncate hover:text-ys-sky text-left">{r.listing_title}</button>
                      <div className="flex gap-0.5 shrink-0">{[1, 2, 3, 4, 5].map((sN) => <Star key={sN} className={`w-4 h-4 ${sN <= r.stars ? "fill-ys-gold text-ys-gold" : "text-slate-200"}`} />)}</div>
                    </div>
                    {r.comment && <p className="text-sm text-slate-600 mt-1.5">{r.comment}</p>}
                    {r.hidden && <Badge className="mt-2 bg-slate-100 text-slate-500">Hidden by admin</Badge>}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Receipt className="w-5 h-5 text-ys-sky" />Billing &amp; charges</h2>
            {charges.length === 0 ? (
              <Card className="p-6 rounded-2xl text-center text-slate-400" data-testid="charges-empty">No charges yet. Your listing, plan and highlight payments will show here.</Card>
            ) : (
              <Card className="rounded-2xl overflow-hidden" data-testid="charges-list">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Item</th><th className="text-left p-3 hidden sm:table-cell">Sale</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Status</th></tr></thead>
                  <tbody>
                    {charges.map((c, i) => (
                      <tr key={c.id || c.session_id || `${c.date}-${c.item}-${c.amount}`} className="border-t border-slate-100" data-testid={`charge-row-${i}`}>
                        <td className="p-3 whitespace-nowrap">{c.date ? new Date(c.date).toLocaleDateString() : "—"}</td>
                        <td className="p-3">{c.item}</td>
                        <td className="p-3 hidden sm:table-cell text-slate-500 truncate max-w-[160px]">{c.listing_title || "—"}</td>
                        <td className="p-3 text-right font-semibold">${Number(c.amount || 0).toFixed(2)}</td>
                        <td className="p-3 text-right"><Badge className={c.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{c.status || "pending"}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="feedback" className="mt-6 space-y-6">
            <Card className="p-6 rounded-2xl" data-testid="suggestion-box">
              <h2 className="font-display text-lg font-bold flex items-center gap-2"><MessageSquarePlus className="w-5 h-5 text-ys-sky" />Suggest an idea</h2>
              <p className="text-sm text-slate-500 mt-1">Members can send ideas straight to the Yard-skip team.</p>
              <Textarea value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="I'd love it if Yard-skip could…" data-testid="suggestion-input" className="mt-3 rounded-xl min-h-[110px]" />
              <Button onClick={submitIdea} data-testid="suggestion-submit" className="mt-3 rounded-full bg-ys-sky hover:bg-sky-700 gap-1"><Send className="w-4 h-4" />Send to the team</Button>
            </Card>
            <Card className="p-6 rounded-2xl" data-testid="site-review-box">
              <h2 className="font-display text-lg font-bold flex items-center gap-2"><Star className="w-5 h-5 text-ys-gold" />Rate Yard-skip</h2>
              <p className="text-sm text-slate-500 mt-1">Tell everyone how we're doing — your review may appear on the landing page.</p>
              <div className="flex gap-1 mt-3" data-testid="site-stars">
                {[1, 2, 3, 4, 5].map((s) => (<button key={s} type="button" onClick={() => setSiteStars(s)} data-testid={`site-star-${s}`}><Star className={`w-7 h-7 ${s <= siteStars ? "fill-ys-gold text-ys-gold" : "text-slate-300"}`} /></button>))}
              </div>
              <Textarea value={siteComment} onChange={(e) => setSiteComment(e.target.value)} placeholder="What do you love about Yard-skip?" data-testid="site-comment-input" className="mt-3 rounded-xl" />
              <Button onClick={submitSiteReview} data-testid="site-review-submit" className="mt-3 rounded-full bg-ys-sky hover:bg-sky-700">Post my review</Button>
            </Card>
          </TabsContent>

          <TabsContent value="listings" className="mt-6">
        {/* My listings */}
        {(user.role === "seller" || user.role === "admin") && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">My listings</h2>
              <Button onClick={() => navigate("/create")} className="rounded-full bg-ys-sky hover:bg-sky-700 gap-1" data-testid="new-listing-btn"><Tag className="w-4 h-4" />New</Button>
            </div>
            {listings.length === 0 ? (
              <Card className="p-8 rounded-2xl text-center text-slate-400"><MapPin className="w-10 h-10 mx-auto mb-2" /><p>No listings yet.</p></Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {listings.map((l) => (
                  <Card key={l.id} className="p-4 rounded-2xl" data-testid={`my-listing-${l.id}`}>
                    <div className="flex gap-3">
                      <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                        {l.photos?.length ? <img src={fileUrl(l.photos[l.profile_photo_index] || l.photos[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin className="w-6 h-6" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold truncate">{l.title}</p>
                          {l.highlighted && <Sparkles className="w-4 h-4 text-ys-gold shrink-0" />}
                        </div>
                        <p className="text-xs text-slate-500">{l.city}, {l.zip}</p>
                        <Badge className={`mt-1.5 ${l.status === "active" ? "bg-emerald-100 text-emerald-700" : l.status === "pending_payment" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          {l.status === "pending_payment" ? "Needs plan" : l.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {l.status === "pending_payment" && <Button size="sm" onClick={() => activate(l.id)} className="rounded-full h-8 text-xs bg-ys-sky hover:bg-sky-700" data-testid={`activate-${l.id}`}>Activate ($5/wk)</Button>}
                      <Button size="sm" variant="outline" onClick={() => navigate(`/edit/${l.id}`)} className="rounded-full h-8 text-xs gap-1" data-testid={`edit-${l.id}`}><Pencil className="w-3 h-3" />Edit</Button>
                      {!l.highlighted && l.status === "active" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => highlight(l.id, "highlight_weekly")} className="rounded-full h-8 text-xs gap-1 text-ys-gold border-ys-gold/40" data-testid={`highlight-week-${l.id}`}><Sparkles className="w-3 h-3" />Highlight my goodies $5/wk</Button>
                          <Button size="sm" variant="outline" onClick={() => highlight(l.id, "highlight_monthly")} className="rounded-full h-8 text-xs gap-1 text-ys-gold border-ys-gold/40" data-testid={`highlight-month-${l.id}`}><Sparkles className="w-3 h-3" />$20/mo</Button>
                        </>
                      )}
                      {l.highlighted && (
                        <Button size="sm" variant="outline" onClick={() => unhighlight(l.id)} data-testid={`unhighlight-${l.id}`} className="rounded-full h-8 text-xs gap-1">Turn off highlight</Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full h-8 text-xs text-red-500 gap-1" data-testid={`delete-${l.id}`}><Trash2 className="w-3 h-3" />Delete</Button></AlertDialogTrigger>
                        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this listing?</AlertDialogTitle><AlertDialogDescription>This can't be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(l.id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
